import { computed } from "vue";
import { useRouter } from "vue-router";
import { TicketsModuleFilter, TicketStatus } from "@/constants/tickets";
import { SubscriptionType } from "@/constants/workplaces";
import { trackGtmEvent } from "@/_helpers/analytics";
import { RouteName } from "@/constants/routes";
import { GtmAction, GTMParams } from "@/constants/gtm.ts";
import startCase from "lodash/startCase";
import { useWorkspaceStore } from "@/_store/workspace.module.ts";
import type { WidgetVulnerability } from "@/_store/dashboard/dashboard.module.ts";
import { isAccessRestricted } from "@/_helpers/permissions.ts";

export function useWidgetContent() {
  const router = useRouter();
  const workspaceStore = useWorkspaceStore();
  const currentPlan = computed(() => workspaceStore.workspace.subscriptionType);

  const getProtectedUsersOnlyFilter = (widget: TicketsModuleFilter) => {
    if (currentPlan.value === SubscriptionType.TRIAL) {
      return false;
    }
    return ![
      TicketsModuleFilter.ENDPOINT_DATA_GOVERNANCE,
      TicketsModuleFilter.DEVICE_WIDGET,
    ].includes(widget);
  };

  const viewVulnerability = async (
    event: { vulnerability: WidgetVulnerability; isActive: boolean },
    widget: TicketsModuleFilter
  ) => {
    await router.push({
      name: RouteName.TICKETS,
      query: {
        eventTriggers: event.vulnerability.issue,
        from: "widget",
        status: event.isActive ? TicketStatus.OPEN : TicketStatus.CLOSED,
        widget,
        protectedUsersOnly: getProtectedUsersOnlyFilter(widget).toString(),
      },
    });
    trackGtmEvent(
      GtmAction.WORKSPACE_VIEW_DASHBOARD_WIDGET,
      `${startCase(widget)} - ${startCase(event.vulnerability.issue)}`
    );
  };

  const viewAllTickets = async (isActive: boolean, widget: TicketsModuleFilter) => {
    await router.push({
      name: RouteName.TICKETS,
      query: {
        from: "widget",
        status: isActive ? TicketStatus.OPEN : TicketStatus.CLOSED,
        widget,
        protectedUsersOnly: getProtectedUsersOnlyFilter(widget).toString(),
      },
    });
    const gtmParam = isActive ? "Open" : "Closed";
    trackGtmEvent(
      GtmAction.WORKSPACE_VIEW_DASHBOARD_WIDGET,
      `${startCase(widget)} - ${GTMParams.ALL} - ${gtmParam}`
    );
  };

  return {
    currentPlan,
    getProtectedUsersOnlyFilter,
    viewVulnerability,
    viewAllTickets,
    isAccessRestricted,
  };
}
